import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supportApi } from '@/api/support';
import { ArrowLeft, DirectInbox } from '@/components/icons';
import { EmptyState, FilterChipBar, ListSection, PageShell } from '@/components/system';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import { cn } from '@/lib/utils';
import { SupportThread } from './components/SupportThread';
import { SupportTicketCard } from './components/SupportTicketCard';
import { getSupportStatusLabel } from './constants';
import type { SupportTicketListStatus, SupportTicketStatus } from '@/api/support';

const FILTERS: Array<{ value: SupportTicketListStatus; label: string }> = [
    { value: 'waiting_support', label: 'Aguardando suporte' },
    { value: 'waiting_user', label: 'Aguardando psicólogo' },
    { value: 'closed', label: 'Fechados' },
    { value: 'all', label: 'Todos' },
];

const TICKET_SEARCH_PARAM = 'ticket';

/** Fila (~340px) + gap + uma conversa que ainda dá para ler (~480px). */
const SPLIT_MIN_WIDTH = 880;

/**
 * Mede o espaço real disponível em vez de olhar a viewport: a sidebar alterna
 * entre 16rem e 3rem, então a mesma largura de tela pode ou não comportar dois
 * painéis. Um breakpoint de viewport erra nos dois sentidos — num iPad de 820px
 * ele espremia a conversa em ~200px e recortava cada mensagem.
 */
const useHasRoomForSplit = () => {
    const observerRef = useRef<ResizeObserver | null>(null);
    const [hasRoom, setHasRoom] = useState<boolean | null>(null);

    // Callback ref, não `useRef` + efeito: a página devolve `<Loading />` antes
    // da grid existir, então um efeito de mount rodaria com a ref ainda vazia e
    // nunca mais voltaria.
    const containerRef = useCallback((node: HTMLDivElement | null) => {
        observerRef.current?.disconnect();
        observerRef.current = null;

        if (!node) return;

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setHasRoom(entry.contentRect.width >= SPLIT_MIN_WIDTH);
            }
        });

        observer.observe(node);
        observerRef.current = observer;
        // Medida síncrona: o celular nunca chega a pintar o split.
        setHasRoom(node.getBoundingClientRect().width >= SPLIT_MIN_WIDTH);
    }, []);

    useEffect(() => () => observerRef.current?.disconnect(), []);

    return [containerRef, hasRoom] as const;
};

const SUPPORT_AGENT_ME_QUERY_KEY = ['support', 'agent', 'me'] as const;
const SUPPORT_AGENT_TICKETS_QUERY_KEY = ['support', 'agent', 'tickets'] as const;
const SUPPORT_AGENT_TICKET_QUERY_KEY = ['support', 'agent', 'ticket'] as const;

const getErrorStatus = (error: unknown) => {
    if (isAxiosError(error)) {
        return error.response?.status;
    }

    if (typeof error === 'object' && error !== null && 'status' in error) {
        const status = (error as { status?: unknown }).status;
        return typeof status === 'number' ? status : undefined;
    }

    return undefined;
};

const isForbiddenError = (error: unknown) => getErrorStatus(error) === 403;

export default function SupportInboxPage() {
    const queryClient = useQueryClient();
    const [splitContainerRef, hasRoomForSplit] = useHasRoomForSplit();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [statusFilter, setStatusFilter] = useState<SupportTicketListStatus>('waiting_support');
    const [redactingMessageId, setRedactingMessageId] = useState<string | null>(null);

    // Selection lives in the URL so the phone's back gesture leaves the thread
    // instead of the whole inbox, and so agents can hand each other a link to
    // one conversation.
    const selectedTicketId = searchParams.get(TICKET_SEARCH_PARAM);

    const selectTicket = useCallback(
        (ticketId: string | null, replace = false) => {
            setSearchParams(
                (current) => {
                    const next = new URLSearchParams(current);

                    if (ticketId) {
                        next.set(TICKET_SEARCH_PARAM, ticketId);
                    } else {
                        next.delete(TICKET_SEARCH_PARAM);
                    }

                    return next;
                },
                { replace },
            );
        },
        [setSearchParams],
    );

    // A deep link has no history entry to pop; clearing the param is the
    // equivalent exit. Popping unconditionally would walk out of the app.
    const goBackToQueue = useCallback(() => {
        if (location.key !== 'default') {
            navigate(-1);
            return;
        }

        selectTicket(null, true);
    }, [location.key, navigate, selectTicket]);

    const handleAgentAccessDenied = useCallback(() => {
        queryClient.removeQueries({ queryKey: SUPPORT_AGENT_TICKETS_QUERY_KEY });
        queryClient.removeQueries({ queryKey: SUPPORT_AGENT_TICKET_QUERY_KEY });
        queryClient.setQueryData(SUPPORT_AGENT_ME_QUERY_KEY, { is_agent: false });
        selectTicket(null, true);
        setRedactingMessageId(null);
        toast.error('Acesso ao inbox de suporte negado.');
    }, [queryClient, selectTicket]);

    const { data: agentStatus, isLoading: isLoadingAgentStatus } = useQuery({
        queryKey: SUPPORT_AGENT_ME_QUERY_KEY,
        queryFn: supportApi.getAgentStatus,
        retry: false,
    });

    const isAgent = agentStatus?.is_agent === true;

    const {
        data: tickets = [],
        isLoading: isLoadingTickets,
        error: ticketsError,
    } = useQuery({
        queryKey: ['support', 'agent', 'tickets', statusFilter],
        queryFn: () => supportApi.listAgentTickets(statusFilter),
        enabled: isAgent,
        refetchInterval: 30_000,
        retry: (failureCount, error) => !isForbiddenError(error) && failureCount < 3,
    });

    // Auto-selection is a two-pane affordance: quando a fila *é* a tela, pré-
    // selecionar jogaria o agente direto numa conversa que ele não escolheu.
    // Toda escrita usa `replace` para o poll de 30s não encher o histórico.
    useEffect(() => {
        if (hasRoomForSplit !== true) return;

        if (tickets.length === 0) {
            if (selectedTicketId) {
                selectTicket(null, true);
            }

            return;
        }

        if (!selectedTicketId || !tickets.some((ticket) => ticket.id === selectedTicketId)) {
            selectTicket(tickets[0].id, true);
        }
    }, [hasRoomForSplit, selectTicket, selectedTicketId, tickets]);

    const ticketQueryKey = ['support', 'agent', 'ticket', selectedTicketId];
    const {
        data: selectedTicket,
        isLoading: isLoadingSelectedTicket,
        error: selectedTicketError,
    } = useQuery({
        queryKey: ticketQueryKey,
        queryFn: () => supportApi.getAgentTicket(selectedTicketId as string),
        enabled: isAgent && Boolean(selectedTicketId),
        refetchInterval: 5_000,
        retry: (failureCount, error) => !isForbiddenError(error) && failureCount < 3,
    });

    useEffect(() => {
        if (isForbiddenError(ticketsError) || isForbiddenError(selectedTicketError)) {
            handleAgentAccessDenied();
        }
    }, [handleAgentAccessDenied, selectedTicketError, ticketsError]);

    const invalidateInbox = () => {
        queryClient.invalidateQueries({ queryKey: ['support', 'agent'] });
    };

    const sendMessageMutation = useMutation({
        mutationFn: (body: string) =>
            supportApi.addAgentTicketMessage(selectedTicketId as string, body),
        onSuccess: (updatedTicket) => {
            queryClient.setQueryData(ticketQueryKey, updatedTicket);
            invalidateInbox();
        },
        onError: (error) => {
            if (isForbiddenError(error)) {
                handleAgentAccessDenied();
                return;
            }

            toast.error('Não foi possível responder o chamado.');
        },
    });

    const statusMutation = useMutation({
        mutationFn: (status: SupportTicketStatus) =>
            supportApi.updateAgentTicketStatus(selectedTicketId as string, status),
        onSuccess: (updatedTicket) => {
            queryClient.setQueryData(ticketQueryKey, updatedTicket);
            invalidateInbox();
            toast.success(
                updatedTicket.status === 'closed'
                    ? 'Chamado fechado.'
                    : `Status alterado para ${getSupportStatusLabel(
                          updatedTicket.status,
                          'agent',
                      ).toLowerCase()}.`,
            );
        },
        onError: (error) => {
            if (isForbiddenError(error)) {
                handleAgentAccessDenied();
                return;
            }

            toast.error('Não foi possível alterar o status.');
        },
    });

    const redactMutation = useMutation({
        mutationFn: supportApi.redactMessage,
        onMutate: (messageId) => {
            setRedactingMessageId(messageId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ticketQueryKey });
            toast.success('Mensagem redigida.');
        },
        onError: (error) => {
            if (isForbiddenError(error)) {
                handleAgentAccessDenied();
                return;
            }

            toast.error('Não foi possível redigir a mensagem.');
        },
        onSettled: () => {
            setRedactingMessageId(null);
        },
    });

    if (isLoadingAgentStatus) {
        return <Loading />;
    }

    if (!isAgent) {
        return <Navigate to="/" replace />;
    }

    // Sem espaço para o split, os dois painéis viram duas telas: a fila e, ao
    // tocar num chamado, a conversa inteira. Uma medida só governa o layout e a
    // navegação, então não existe largura em que CSS e JS discordem.
    const isDrilldown = hasRoomForSplit === false;
    const showList = !isDrilldown || !selectedTicketId;
    const showThread = !isDrilldown || Boolean(selectedTicketId);

    return (
        <PageShell fill width="full">
            <div
                ref={splitContainerRef}
                className={cn(
                    'grid min-h-0 flex-1 overflow-hidden',
                    hasRoomForSplit
                        ? 'grid-cols-[minmax(300px,360px)_minmax(0,1fr)] gap-6'
                        : 'grid-cols-1 gap-4',
                )}
            >
                <ListSection
                    title="Chamados"
                    description="Fila interna de suporte"
                    contentClassName="min-h-0 flex-1 p-3 md:p-4"
                    filters={
                        <FilterChipBar
                            ariaLabel="Filtrar chamados de suporte"
                            value={statusFilter}
                            onChange={(nextStatus) => {
                                setStatusFilter(nextStatus as SupportTicketListStatus);
                                selectTicket(null, true);
                            }}
                            className="w-full flex-nowrap gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0"
                            options={FILTERS}
                        />
                    }
                    className={cn('flex min-h-0 min-w-0 flex-col', !showList && 'hidden')}
                >
                    {isLoadingTickets ? (
                        <Loading className="h-auto py-8" size={120} />
                    ) : tickets.length === 0 ? (
                        <EmptyState
                            icon={DirectInbox}
                            title="Fila vazia"
                            description="Não há chamados nesse filtro no momento."
                            className="shadow-none"
                        />
                    ) : (
                        <div className="h-full min-h-0 space-y-3 overflow-y-auto pr-1">
                            {tickets.map((ticket) => (
                                <SupportTicketCard
                                    key={ticket.id}
                                    ticket={ticket}
                                    perspective="agent"
                                    selected={ticket.id === selectedTicketId}
                                    onClick={() => selectTicket(ticket.id)}
                                />
                            ))}
                        </div>
                    )}
                </ListSection>

                <div
                    className={cn(
                        'flex min-h-0 min-w-0',
                        !showThread && 'hidden',
                        !selectedTicketId &&
                            'items-center justify-center rounded-2xl border border-dashed border-border bg-surface',
                    )}
                >
                    {!selectedTicketId ? (
                        <EmptyState
                            icon={DirectInbox}
                            title="Selecione um chamado"
                            description="Escolha uma conversa da fila para ver o histórico e responder."
                            className="border-0 bg-transparent shadow-none"
                        />
                    ) : isLoadingSelectedTicket || !selectedTicket ? (
                        <Loading />
                    ) : (
                        <SupportThread
                            ticket={selectedTicket}
                            perspective="agent"
                            isSending={sendMessageMutation.isPending}
                            onSendMessage={(body) => sendMessageMutation.mutateAsync(body)}
                            onCloseTicket={() => statusMutation.mutateAsync('closed')}
                            isClosing={statusMutation.isPending}
                            onRedactMessage={(messageId) => redactMutation.mutateAsync(messageId)}
                            isRedactingMessageId={redactingMessageId}
                            headerLeading={
                                isDrilldown ? (
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="size-10"
                                        onClick={goBackToQueue}
                                        aria-label="Voltar para a fila"
                                        title="Voltar para a fila"
                                    >
                                        <ArrowLeft className="size-4" />
                                    </Button>
                                ) : undefined
                            }
                        />
                    )}
                </div>
            </div>
        </PageShell>
    );
}
